import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { type UseFormProps, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useFormWithDraft } from '~/hooks/use-draft-form';
import { useDialoger } from '~/modules/common/dialoger/use-dialoger';
import InputFormField from '~/modules/common/form-fields/input';
import SelectParentFormField from '~/modules/common/form-fields/select-parent';
import { SlugFormField } from '~/modules/common/form-fields/slug';
import CreateOrganizationForm from '~/modules/organizations/create-organization-form';
import type { Organization } from '~/modules/organizations/types';
import { Alert, AlertDescription, AlertTitle } from '~/modules/ui/alert';
import { Button, SubmitButton } from '~/modules/ui/button';
import { Form } from '~/modules/ui/form';
import { useWorkspaceCreateMutation } from '~/modules/workspaces/query';
import type { Workspace } from '~/modules/workspaces/types';
import { useNavigationStore } from '~/store/navigation';
import { createWorkspaceBodySchema } from '#/modules/workspaces/schema';
import { idSchema } from '#/utils/schema/common';

interface CreateWorkspaceFormProps {
  callback?: (workspace: Workspace) => void;
  dialog?: boolean;
}

const formSchema = createWorkspaceBodySchema.setKey('organizationId', idSchema);

type FormValues = z.infer<typeof formSchema>;

const CreateWorkspaceForm = ({ callback, dialog: isDialog }: CreateWorkspaceFormProps) => {
  const { t } = useTranslation();

  const { menu } = useNavigationStore();

  const organizations = menu.organizations;
  const formOptions: UseFormProps<FormValues> = useMemo(
    () => ({
      resolver: zodResolver(formSchema),
      defaultValues: {
        name: '',
        slug: '',
        organizationId: organizations.length >= 1 ? organizations[0].id : '',
      },
    }),
    [],
  );

  // Form with draft in local storage
  const form = useFormWithDraft<FormValues>('create-workspace', { formOptions });

  // Watch to update slug field
  const name = useWatch({ control: form.control, name: 'name' });

  const { mutate: create, isPending } = useWorkspaceCreateMutation();

  const onSubmit = (values: FormValues) => {
    create(values, {
      onSuccess: (createdWorkspace) => {
        form.reset();
        if (isDialog) useDialoger.getState().remove();

        callback?.(createdWorkspace);
      },
    });
  };

  const organizationCreated = (organization: Organization) => {
    form.setValue('organizationId', organization.id);
  };

  // Alert if no organizations exist
  if (!form.getValues('organizationId') && !menu.organizations.length)
    return (
      <Alert variant="plain" className="w-auto">
        <AlertTitle>{t('common:resource_required', { resource: t('common:organization') })}</AlertTitle>
        <AlertDescription className="pr-8 font-light">
          <p className="mb-2">{t('app:organization_required.text')}</p>
          <Button
            onClick={() => {
              useDialoger.getState().create(<CreateOrganizationForm callback={organizationCreated} dialog />, {
                className: 'md:max-w-2xl',
                id: 'create-organization',
                title: t('common:create_resource', { resource: t('common:organization').toLowerCase() }),
              });
            }}
          >
            <span>{t('common:create_resource', { resource: t('common:organization').toLowerCase() })}</span>
          </Button>
        </AlertDescription>
      </Alert>
    );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <InputFormField control={form.control} name="name" label={t('common:name')} required />
        <SlugFormField
          control={form.control}
          type="workspace"
          label={t('common:resource_handle', { resource: t('app:workspace') })}
          description={t('common:resource_handle.text', { resource: t('app:workspace').toLowerCase() })}
          nameValue={name}
        />

        <SelectParentFormField
          collection="organizations"
          type="organization"
          control={form.control}
          label={t('common:organization')}
          name="organizationId"
          required
        />

        <div className="flex flex-col sm:flex-row gap-2">
          <SubmitButton disabled={!form.formState.isDirty} loading={isPending}>
            {t('common:create')}
          </SubmitButton>
          <Button
            type="reset"
            variant="secondary"
            className={form.formState.isDirty ? '' : 'invisible'}
            aria-label="Cancel"
            onClick={() => form.reset()}
          >
            {t('common:cancel')}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CreateWorkspaceForm;
